<template>
  <div class="form-grid">
    <!-- Left Column -->
    <div class="form-column">
      <label>
        Enter Binance email
        <input
          :value="formData.binance_email"
          type="email"
          placeholder="Enter Your Email Used In Binance"
          required
          @input="$emit('update:binance_email', $event.target.value)"
        />
        <span v-if="errors.binance_email" class="error-message">
          {{ errors.binance_email }}
        </span>
      </label>

      <label>
        Phone number
        <div class="phone-input-group">
          <select
            :value="selectedCountry"
            class="country-select"
            @change="$emit('country-changed', $event.target.value)"
          >
            <option
              v-for="country in countries"
              :key="country.code"
              :value="country.code"
            >
              {{ country.emoji }} +{{ country.dialCode }}
            </option>
          </select>
          <input
            :value="nationalPhoneNumber"
            type="tel"
            placeholder="Phone number"
            @input="$emit('phone-input', $event.target.value)"
            class="phone-input"
            :class="{
              'is-invalid': phoneInputTouched && !isPhoneValid,
              'is-valid': phoneInputTouched && isPhoneValid,
            }"
            required
          />
        </div>
        <span v-if="errors.phone_number" class="error-message">
          {{ errors.phone_number }}
        </span>
        <small
          v-if="phoneInputTouched && !isPhoneValid && !errors.phone_number"
          class="text-danger"
        >
          Please enter a valid phone number
        </small>
        <small v-if="phoneInputTouched && isPhoneValid" class="text-success">
          Valid phone number format
        </small>
      </label>

      <label>
        Binance public wallet address / Id
        <input
          :value="formData.wallet_address"
          type="text"
          placeholder="Wallet Address Or Id"
          required
          @input="$emit('update:wallet_address', $event.target.value)"
        />
        <span v-if="errors.wallet_address" class="error-message">
          {{ errors.wallet_address }}
        </span>
      </label>

      <label class="otp-wrapper">
        Phone OTP code
        <input
          :value="formData.phone_otp"
          type="text"
          placeholder="Enter OTP Code"
          required
          @input="$emit('update:phone_otp', $event.target.value)"
        />
        <button
          type="button"
          class="otp-btn"
          @click="$emit('request-phone-otp')"
          :disabled="isPhoneOTPRequested || !isPhoneValid"
        >
          {{ phoneOTPButtonText }}
        </button>
        <span v-if="errors.phone_otp" class="error-message">
          {{ errors.phone_otp }}
        </span>
      </label>

      <label class="otp-wrapper">
        Email OTP code
        <input
          :value="formData.email_otp"
          type="text"
          placeholder="Enter OTP Code"
          required
          @input="$emit('update:email_otp', $event.target.value)"
        />
        <button
          type="button"
          class="otp-btn"
          @click="$emit('request-email-otp')"
          :disabled="isEmailOTPRequested"
        >
          {{ emailOTPButtonText }}
        </button>
        <span v-if="errors.email_otp" class="error-message">
          {{ errors.email_otp }}
        </span>
      </label>
    </div>

    <!-- Right Column -->
    <div class="form-column">
      <label>
        Passport Proof
        <div class="custom-upload">
          <input
            type="file"
            id="passportProof"
            @change="handlePassportUpload"
            accept="image/*,.pdf"
          />
          <div class="upload-box">
            <svg
              class="upload-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M5 20h14v-2H5v2zm7-18L5.33 11h4.17v4h4.99v-4h4.17L12 2z"
              />
            </svg>
            <p>
              {{ passportFileName || "Click to upload or drag & drop" }}
            </p>
          </div>
        </div>
        <span v-if="errors.passport_proof" class="error-message">
          {{ errors.passport_proof }}
        </span>
      </label>

      <label>
        Binance account verification center screenshot
        <div class="custom-upload">
          <input
            type="file"
            id="binanceScreenshot"
            @change="handleBinanceScreenshotUpload"
            accept="image/*"
          />
          <div class="upload-box">
            <svg
              class="upload-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M5 20h14v-2H5v2zm7-18L5.33 11h4.17v4h4.99v-4h4.17L12 2z"
              />
            </svg>
            <p>
              {{
                binanceScreenshotFileName || "Click to upload or drag & drop"
              }}
            </p>
          </div>
        </div>
        <span v-if="errors.binance_screenshot" class="error-message">
          {{ errors.binance_screenshot }}
        </span>
      </label>
    </div>
  </div>
</template>

<script>
export default {
  name: "VerificationForm",
  props: {
    formData: {
      type: Object,
      required: true,
    },
    selectedCountry: {
      type: String,
      required: true,
    },
    nationalPhoneNumber: {
      type: String,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
    isPhoneValid: {
      type: Boolean,
      required: true,
    },
    phoneInputTouched: {
      type: Boolean,
      required: true,
    },
    isPhoneOTPRequested: {
      type: Boolean,
      required: true,
    },
    isEmailOTPRequested: {
      type: Boolean,
      required: true,
    },
    phoneOTPButtonText: {
      type: String,
      required: true,
    },
    emailOTPButtonText: {
      type: String,
      required: true,
    },
    passportFileName: {
      type: String,
      required: true,
    },
    binanceScreenshotFileName: {
      type: String,
      required: true,
    },
  },
  methods: {
    handlePassportUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.$emit("passport-uploaded", file);
      }
    },
    handleBinanceScreenshotUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.$emit("binance-screenshot-uploaded", file);
      }
    },
  },
};
</script>
